import React, { Component } from 'react';

import Icon from '../../modules/core/components/Icon';

/**
 * ErrorBoundary — перехват ошибок рендера React-дерева.
 *
 * Обязан быть классовым компонентом: React требует componentDidCatch / getDerivedStateFromError.
 *
 * @param {string}   name    — имя boundary для логов (например, 'global', 'orders-list')
 * @param {function} onError — колбэк для внешней системы мониторинга (Sentry/Logstash).
 *                             Пока Sentry не подключён — можно не передавать.
 */
  interface ErrorBoundaryProps {
    name?: string
    onError?(...args: unknown[]): unknown
    children: React.ReactNode
  }

  interface ErrorBoundaryState {
    hasError: boolean
    error: Error | null
    errorInfo: React.ErrorInfo | null
  }

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static defaultProps: Partial<ErrorBoundaryProps> = {
    name: null,
    onError: null,
  };

  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { name, onError } = this.props;
    const label = name ? `ErrorBoundary:${ name }` : 'ErrorBoundary';

    // eslint-disable-next-line no-console
    console.error(`[${ label }] Перехвачена ошибка:`, error, errorInfo);

    if (typeof onError === 'function') {
      onError(error, errorInfo);
    }

    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, name } = this.props;

    if (hasError) {
      return (
        <div className='container mt-5'>
          <div className='row justify-content-center'>
            <div className='col-md-8 col-lg-6'>
              <div className='alert alert-danger' role='alert'>
                <h4 className='alert-heading'>
                  <Icon name='exclamation-triangle' className='mr-2' />
                  Что-то пошло не так
                </h4>
                <p className='mb-3'>
                  { name
                    ? (
                      <>
                        Произошла ошибка при загрузке раздела
                        { ' ' }
                        <strong>«{ name }»</strong>
                        .
                      </>
                    )
                    : 'Произошла непредвиденная ошибка приложения.' }
                </p>
                <div>
                  <button
                    type='button'
                    className='btn btn-primary mr-2'
                    onClick={ this.handleReload }
                  >
                    <Icon name='refresh' className='mr-1' />
                    Перезагрузить страницу
                  </button>
                  <a
                    href='/'
                    className='btn btn-secondary'
                  >
                    <Icon name='home' className='mr-1' />
                    На главную
                  </a>
                </div>
              </div>

              { process.env.NODE_ENV === 'development' && error && (
                <details className='mt-3'>
                  <summary className='text-muted' style={ { cursor: 'pointer' } }>
                    <small>Детали ошибки (только для разработки)</small>
                  </summary>
                  <div
                    className='mt-2 p-3 bg-light border rounded'
                    style={ { fontSize: '0.85rem' } }
                  >
                    <strong>Ошибка:</strong>
                    <pre className='mt-1 mb-2 text-danger' style={ { whiteSpace: 'pre-wrap' } }>
                      { error.toString() }
                    </pre>
                    { errorInfo?.componentStack && (
                      <>
                        <strong>Стек компонентов:</strong>
                        <pre className='mt-1 mb-0 text-muted' style={ { whiteSpace: 'pre-wrap' } }>
                          { errorInfo.componentStack }
                        </pre>
                      </>
                    ) }
                  </div>
                </details>
              ) }
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;